import { getAccountSnapshot, getSogniClientInstance, isClientReady } from '../sogni-client.svelte.js';
import { assertRequestCurrent } from './generation-request.js';
import { getGenerationErrorDetails } from './generation-error-details.js';

// The socket shares a 20-per-10-second owner read budget across capacity,
// active projects and sync. Admission waves share one request and space reads.
let pendingRead = null;
let pendingOwner = null;
let lastReadAt = 0;
let retryAt = 0;
let retryOwner = null;
let lastReadError = null;

export async function readGenerationCapacity(request = null) {
  const snapshot = getAccountSnapshot();
  const owner = snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() : null;
  if (!owner || !isClientReady()) {
    throw Object.assign(new Error('Sign in to check your account capacity.'), { code: 'AUTH_REQUIRED' });
  }
  const assertOwner = () => {
    const current = getAccountSnapshot();
    const accountId = current.authenticated ? current.walletAddress?.toLowerCase() : null;
    if (request) assertRequestCurrent(request, { accountId, sourceFingerprint: request.sourceFingerprint,
      subscription: current.subscription });
    else if (accountId !== owner) {
      throw Object.assign(new Error('Your account changed while capacity was loading.'), { code: 'SESSION_IDENTITY_CHANGED' });
    }
  };
  assertOwner();
  if (owner === retryOwner && Date.now() < retryAt) throw lastReadError;
  if (pendingRead && pendingOwner === owner) {
    const capacity = await pendingRead;
    assertOwner();
    return capacity;
  }
  const read = (async () => {
    const delay = Math.max(0, 1000 - (Date.now() - lastReadAt));
    if (delay) await new Promise(resolve => setTimeout(resolve, delay));
    assertOwner();
    lastReadAt = Date.now();
    // RestClient.get returns the socket endpoint's JSON body directly.
    let capacity;
    try {
      capacity = await getSogniClientInstance().apiClient.socket.get('/api/v1/artist/generation-capacity');
    } catch (error) {
      const details = getGenerationErrorDetails(error);
      const retryAfter = Number(details.retryAfter);
      const retryDelay = retryAfter > 0 ? retryAfter * 1000 : Number(details.status) === 429 ? 30000 : 0;
      if (retryDelay > 0) { retryAt = Date.now() + retryDelay; retryOwner = owner; lastReadError = error; }
      throw error;
    }
    assertOwner();
    return capacity;
  })();
  pendingOwner = owner;
  pendingRead = read;
  try { return await read; }
  finally {
    if (pendingRead === read) { pendingRead = null; pendingOwner = null; }
  }
}
